import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import TicketForm from '../../../components/admin/events/TicketForm';
import { useTickets } from '../../../hooks/useTickets';
import { getTickets, createTicket, updateTicket, deleteTicket } from '../../../services/event.service';
import { supabase } from '../../../lib/supabase';
import { Event, Ticket } from '../../../types/event';
import { useToast } from '../../../hooks/useToast';

const EventTickets: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { tickets, loading: ticketsLoading, addTicket, editTicket, removeTicket } = useTickets(id || '');
  const { showToast } = useToast();
  
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTicket, setEditingTicket] = useState<Ticket | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchEvent = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // Récupérer l'événement directement depuis Supabase avec l'ID extrait des paramètres
        console.log('Utilisation de l\'ID extrait des paramètres:', id);
        
        const { data, error } = await supabase
          .from('events')
          .select('*')
          .eq('id', id)
          .single();
        
        if (error) {
          console.error('Erreur Supabase lors de la récupération de l\'événement:', error);
          throw error;
        }
        
        if (!data) {
          console.error('Aucun événement trouvé avec l\'ID:', id);
          throw new Error(`Événement avec l'ID ${id} non trouvé`);
        }
        
        setEvent(data);
        console.log('Événement récupéré directement:', data);
      } catch (error: any) {
        console.error('Erreur lors de la récupération de l\'événement:', error);
        showToast(error.message || 'Erreur lors du chargement de l\'événement', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvent();
  }, [id, showToast]);
  
  const handleAddTicket = async (ticketData: Partial<Ticket>) => {
    try {
      setIsSubmitting(true);
      await addTicket(ticketData);
      showToast('Ticket ajouté avec succès', 'success');
      setShowForm(false);
      setIsSubmitting(false);
    } catch (error: any) {
      showToast(error.message || 'Erreur lors de l\'ajout du ticket', 'error');
      setIsSubmitting(false);
    }
  };
  
  const handleEditTicket = async (ticketData: Partial<Ticket>) => {
    if (!editingTicket) return;
    
    try {
      setIsSubmitting(true);
      await editTicket(editingTicket.id, ticketData);
      showToast('Ticket mis à jour avec succès', 'success');
      setEditingTicket(null);
      setIsSubmitting(false);
    } catch (error: any) {
      showToast(error.message || 'Erreur lors de la mise à jour du ticket', 'error');
      setIsSubmitting(false);
    }
  };
  
  const handleDeleteTicket = async (ticketId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce ticket ? Cette action est irréversible.')) {
      try {
        setDeletingId(ticketId);
        await removeTicket(ticketId);
        showToast('Ticket supprimé avec succès', 'success');
      } catch (error: any) {
        showToast(error.message || 'Erreur lors de la suppression du ticket', 'error');
      } finally {
        setDeletingId(null);
      }
    }
  };
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }
  
  if (!event) {
    return (
      <AdminLayout>
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-900/20 border-l-4 border-red-500 p-4 text-red-200">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-200">
                  Événement non trouvé.
                </p>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <Link
            to="/events/admin"
            className="text-yellow-400 hover:text-yellow-300 flex items-center"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Retour aux événements
          </Link>
        </div>
        
        <div className="sm:flex sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-100">Tickets pour {event.title}</h1>
            <p className="mt-2 text-sm text-gray-300">
              Gérez les tickets disponibles pour cet événement.
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <button
              onClick={() => {
                setShowForm(true);
                setEditingTicket(null);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-yellow-400 hover:bg-yellow-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Ajouter un ticket
            </button>
          </div>
        </div>
        
        {showForm && !editingTicket && (
          <div className="bg-black/90 shadow-sm rounded-lg p-6 mb-8 border border-white/10">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-100">Ajouter un nouveau ticket</h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-200"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <TicketForm
              eventId={event.id}
              onSubmit={handleAddTicket}
              isSubmitting={isSubmitting}
            />
          </div>
        )}
        
        {editingTicket && (
          <div className="bg-black/90 shadow-sm rounded-lg p-6 mb-8 border border-white/10">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-100">Modifier le ticket</h2>
              <button
                onClick={() => setEditingTicket(null)}
                className="text-gray-400 hover:text-gray-200"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <TicketForm
              eventId={event.id}
              initialData={editingTicket}
              onSubmit={handleEditTicket}
              isSubmitting={isSubmitting}
            />
          </div>
        )}
        
        {ticketsLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-400"></div>
          </div>
        ) : tickets.length === 0 ? (
          <div className="bg-black/90 shadow rounded-lg p-6 text-center border border-white/10">
            <svg className="mx-auto h-12 w-12 text-yellow-400/70" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"></path>
            </svg>
            <h3 className="mt-2 text-sm font-medium text-yellow-400">Aucun ticket</h3>
            <p className="mt-1 text-sm text-gray-300">
              Commencez par ajouter un ticket pour cet événement.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-yellow-400 hover:bg-yellow-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Ajouter un ticket
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-black/90 shadow overflow-hidden sm:rounded-md border border-white/10">
            <ul className="divide-y divide-gray-700">
              {tickets.map((ticket, index) => (
                <li key={ticket.id} className={index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center">
                          <h3 className="text-lg font-medium text-gray-100 mr-3">{ticket.name}</h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            ticket.ticket_type === 'vip' ? 'bg-purple-900/30 text-purple-400' :
                            ticket.ticket_type === 'free' ? 'bg-green-900/30 text-green-400' :
                            ticket.ticket_type === 'early_bird' ? 'bg-yellow-900/30 text-yellow-400' :
                            'bg-blue-900/30 text-blue-400'
                          }`}>
                            {ticket.ticket_type === 'standard' ? 'Standard' :
                             ticket.ticket_type === 'vip' ? 'VIP' :
                             ticket.ticket_type === 'free' ? 'Gratuit' :
                             ticket.ticket_type === 'early_bird' ? 'Early Bird' : ticket.ticket_type}
                          </span>
                        </div>
                        
                        {ticket.description && (
                          <p className="mt-1 text-sm text-gray-300">{ticket.description}</p>
                        )}
                        
                        {ticket.benefits && (
                          <p className="mt-1 text-sm text-gray-300">
                            <span className="font-medium">Avantages:</span> {ticket.benefits}
                          </p>
                        )}
                      </div>
                      
                      <div className="text-right">
                        <p className="text-xl font-bold text-gray-100">
                          {ticket.price === 0 ? 'Gratuit' : `${ticket.price.toLocaleString()} ${ticket.currency}`}
                        </p>
                        
                        {ticket.quantity && (
                          <p className="text-sm text-gray-400">
                            Quantité: {ticket.available_quantity !== undefined ? `${ticket.available_quantity}/${ticket.quantity}` : ticket.quantity}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-4 flex justify-end space-x-3">
                      <button
                        onClick={() => setEditingTicket(ticket)}
                        className="inline-flex items-center px-3 py-1.5 border border-white/10 text-sm leading-5 font-medium rounded-md text-gray-200 bg-white/5 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-yellow-400/50"
                      >
                        <svg className="-ml-1 mr-1 h-5 w-5 text-yellow-400/70" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                        </svg>
                        Modifier
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            setIsSubmitting(true);
                            const {id, available_quantity, created_at, updated_at, ...rest} = ticket;
                            const duplicated = {
                              ...rest,
                              name: ticket.name + ' (copie)',
                              available_quantity: ticket.quantity,
                            };
                            await addTicket(duplicated);
                            showToast('Ticket dupliqué avec succès', 'success');
                          } catch (error: any) {
                            showToast(error.message || 'Erreur lors de la duplication', 'error');
                          } finally {
                            setIsSubmitting(false);
                          }
                        }}
                        className="inline-flex items-center px-3 py-1.5 border border-blue-500/30 text-sm leading-5 font-medium rounded-md text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      >
                        <svg className="-ml-1 mr-1 h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16h8M8 12h8m-7 8h6a2 2 0 002-2V7a2 2 0 00-2-2H9a2 2 0 00-2 2v11a2 2 0 002 2z"></path>
                        </svg>
                        Dupliquer
                      </button>
                      <button
                        onClick={() => handleDeleteTicket(ticket.id)}
                        disabled={deletingId === ticket.id}
                        className="inline-flex items-center px-3 py-1.5 border border-red-500/30 text-sm leading-5 font-medium rounded-md text-red-400 bg-red-500/10 hover:bg-red-500/20 focus:outline-none focus:ring-2 focus:ring-red-500/50"
                      >
                        {deletingId === ticket.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-red-400 mr-1"></div>
                        ) : (
                          <svg className="-ml-1 mr-1 h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                          </svg>
                        )}
                        Supprimer
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default EventTickets;
